import { apiRaw } from './api';
import { getPendingEntries, removePendingEntry, updatePendingEntry, type PendingTimeEntry } from './offlineDb';

export async function syncPendingEntries(): Promise<{ synced: number; failed: number; conflicts: number }> {
  const entries = await getPendingEntries();
  const pending = entries.filter((e) => e.status === 'PENDING');
  let synced = 0;
  let failed = 0;
  let conflicts = 0;

  for (const entry of pending) {
    try {
      const res = await apiRaw('/time-entries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: entry.date,
          projectId: entry.projectId,
          taskId: entry.taskId,
          duration: entry.duration,
          note: entry.note,
        }),
      });

      if (res.ok) {
        await removePendingEntry(entry.localId);
        synced++;
      } else if (res.status === 409) {
        const data = await res.json().catch(() => ({}));
        await updatePendingEntry(entry.localId, {
          status: 'CONFLICT',
          error: data?.error || 'Duplicate entry detected',
          duplicateId: data?.duplicateId,
        });
        conflicts++;
      } else if (res.status >= 400 && res.status < 500) {
        const data = await res.json().catch(() => ({}));
        await updatePendingEntry(entry.localId, {
          status: 'FAILED',
          error: data?.error || `Client error: ${res.status}`,
        });
        failed++;
      } else {
        break;
      }
    } catch {
      break;
    }
  }

  if (synced > 0 || conflicts > 0 || failed > 0) {
    window.dispatchEvent(new CustomEvent('jani-sync-complete', { detail: { synced, failed, conflicts } }));
  }

  return { synced, failed, conflicts };
}

export async function forceCreateEntry(entry: PendingTimeEntry): Promise<boolean> {
  const res = await apiRaw('/time-entries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Force-Create': 'true' },
    body: JSON.stringify({
      date: entry.date,
      projectId: entry.projectId,
      taskId: entry.taskId,
      duration: entry.duration,
      note: entry.note,
    }),
  });

  if (res.ok) {
    await removePendingEntry(entry.localId);
    return true;
  }

  // If it fails, mark as FAILED
  const data = await res.json().catch(() => ({}));
  await updatePendingEntry(entry.localId, {
    status: 'FAILED',
    error: data?.error || `Force create failed: ${res.status}`,
  });
  return false;
}
